import { FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import TitlePage from '../../../components/title-page/TitlePage';
import BreadCrumb from '../../../components/title-page/BreadCrumb';
import { ReactComponent as DocumentsIcon } from '../../../assets/images/menu-board-wrapper.svg';
import TemplateBox from '../../form-templates/TemplateBox';
import { DocumentInterface } from '../../../utils/types/document';
import AlertDialog from '../../../components/dialog/AlertDialog';
import { documentInitialState } from '../../../context/reducers/documentReducer';
import { DocumentContext } from '../../../context/documentContext';
import VesselEmptyListService from '../VesselEmptyListService';
import getDocumentsGridColumns from './ListGridColumns';
import TitleGrid from '../../../components/grid/TitleGrid';
import { defaultPagination } from '../../../utils/constants/defaults';
import { Pagination } from '../../../utils/types/pagination';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { VesselContext } from '../../../context/vesselContext';
import { StorableAssetContext } from '../../../context/storableAssetContext';
import { NotificationContext } from '../../../context/notificationContext';
import { Button, SwipeableDrawer } from '@mui/material';
import UpdateDocumentForm from './UpdateDocumentForm';
import DocumentForm from './CreateDocumentForm';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import MrtDataTable from '../../../components/mrt-data-table';
import FormTypesEnum from '../../../enums/formTypes';

const defaultAttributesParams: string[] = [];
const defaultOrderParams: string[] = ['updatedAt:DESC'];
const defaultPaginationParams = {
  ...defaultPagination,
  attributes: defaultAttributesParams,
  join: [],
  order: defaultOrderParams
};

const DocumentsList: FC = (): ReactElement => {
  const query = useQueryParams();

  const {
    listDocuments,
    deleteDocument,
    resetDocumentState,
    state: documentState
  } = useContext(DocumentContext);
  const {
    requestStorableAssetDownloadUrl,
    state: storableAssetState,
    deleteStorableAsset
  } = useContext(StorableAssetContext);
  const { viewVessel, state: vesselState } = useContext(VesselContext);
  const { showAlert } = useContext(NotificationContext);

  const [vesselId] = useState<string>(query.get('vesselId') || '');
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState<boolean>(false);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState<boolean>(false);
  const [openDownloadDialog, setOpenDownloadDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentInterface>(documentInitialState);
  const [paginationParams, setPaginationParams] = useState<Pagination>({
    ...defaultPaginationParams,
    filter: `vessel_id:${vesselId}`
  });

  const pagination: Pagination = {
    ...paginationParams,
    filter: `vessel_id:${vesselId}`
  };

  useEffect(() => {
    if (!vesselState.view.data?.id) viewVessel(vesselId);

    return () => {
      document.body.style.backgroundColor = '#FFF';
    };
  }, []);

  useEffect(() => {
    if (documentState.create.done) {
      showAlert({
        variant: 'success',
        title: 'Document',
        description: 'Document created!'
      });

      resetDocumentState();
      setIsCreateFormOpen(false);
      listDocuments(pagination);
    }

    if (documentState.create.error) {
      showAlert({
        variant: 'error',
        title: 'Document',
        description: "Document couldn't be created!"
      });

      setIsCreateFormOpen(false);
    }
  }, [documentState.create]);

  useEffect(() => {
    if (documentState.delete.done) {
      showAlert({
        variant: 'success',
        title: 'Document',
        description: 'Document deleted!'
      });

      deleteStorableAsset(selectedDocument.storeable_asset_id);
      listDocuments(pagination);
    } else if (documentState.delete.error) {
      showAlert({
        variant: 'error',
        title: 'Document',
        description: "Document couldn't be deleted!"
      });
    }

    setOpenDeleteDialog(false);
  }, [documentState.delete]);

  useEffect(() => {
    if (storableAssetState.requestDownloadUrl.data) {
      setOpenDownloadDialog(false);
      window.open(storableAssetState.requestDownloadUrl.data.downloadUrl, '_blank');
    }
  }, [storableAssetState.requestDownloadUrl]);

  const onDownload = (document: DocumentInterface) => {
    setSelectedDocument(document);
    setOpenDownloadDialog(true);
  };

  const onDelete = (document: DocumentInterface) => {
    setSelectedDocument(document);
    setOpenDeleteDialog(true);
  };

  const onEdit = (document: DocumentInterface) => {
    setSelectedDocument(document);
    setIsUpdateFormOpen(true);
  };

  const onConfirmDownload = () => {
    if (!selectedDocument.storeable_asset_id) return;

    requestStorableAssetDownloadUrl(selectedDocument.storeable_asset_id);
  };

  const handleConfirmDelete = () => {
    deleteDocument(selectedDocument.id);
  };

  const toggleDrawer =
    (isOpen: boolean, formType: FormTypesEnum) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      if (formType === FormTypesEnum.CREATE) {
        setIsCreateFormOpen(isOpen);
      } else if (formType === FormTypesEnum.EDIT) {
        setIsUpdateFormOpen(isOpen);
      }
    };

  const columns = getDocumentsGridColumns(onDownload, onDelete, onEdit);

  useEffect(() => {
    listDocumentsPaginated(paginationParams);
  }, [paginationParams]);

  const listDocumentsPaginated = useCallback(
    (paginationParams: Pagination) => {
      listDocuments(paginationParams);
    },
    [paginationParams]
  );

  const handlePaginationChange = (paginationState: MRT_PaginationState) => {
    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        page: paginationState.pageIndex + 1,
        perPage: paginationState.pageSize
      };
    });
  };

  const handleSortingChange = (sortingState: MRT_SortingState) => {
    const order = sortingState.length
      ? sortingState.map((sort) => `${sort.id}:${sort.desc ? 'DESC' : 'ASC'}`)
      : defaultOrderParams;

    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        order
      };
    });
  };

  return (
    <>
      <TitlePage
        leftContainer={
          <BreadCrumb previous={vesselState.view.data?.name || 'Vessel'} current={'Documents'} />
        }
        rightContainer={
          <Button variant="outlined" onClick={() => setIsCreateFormOpen(true)}>
            Upload document
          </Button>
        }
      />
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        style={{ marginTop: '71px', marginBottom: '49px' }}>
        <TemplateBox height={'calc(100vh - 240px)'}>
          {documentState.list.data?.results?.length === 0 ? (
            <VesselEmptyListService serviceName={'Documents'} />
          ) : (
            <>
              <TitleGrid icon={<DocumentsIcon className="icon-title" />} title={'Documents'} />

              <MrtDataTable
                isLoading={documentState.list.loading}
                isError={Boolean(documentState.list.error)}
                columns={columns}
                rows={documentState?.list?.data?.results}
                rowCount={documentState?.list?.data?.pagination?.total}
                initialPagination={{
                  pageIndex: paginationParams.page - 1,
                  pageSize: paginationParams.perPage
                }}
                onPaginationChange={handlePaginationChange}
                onSortingChange={handleSortingChange}
              />
            </>
          )}
        </TemplateBox>
      </Grid>

      <SwipeableDrawer
        anchor={'bottom'}
        open={isUpdateFormOpen}
        onClose={toggleDrawer(false, FormTypesEnum.EDIT)}
        onOpen={toggleDrawer(true, FormTypesEnum.EDIT)}
        sx={swipeableDrawerStyle}>
        {isUpdateFormOpen && (
          <UpdateDocumentForm
            vesselId={vesselId}
            onClose={() => {
              setIsUpdateFormOpen(false);
              listDocuments(pagination);
            }}
            document={selectedDocument}
          />
        )}
      </SwipeableDrawer>

      <SwipeableDrawer
        anchor={'bottom'}
        open={isCreateFormOpen}
        onClose={toggleDrawer(false, FormTypesEnum.CREATE)}
        onOpen={toggleDrawer(true, FormTypesEnum.CREATE)}
        sx={swipeableDrawerStyle}>
        {isCreateFormOpen && (
          <DocumentForm
            vesselId={vesselId}
            onClose={() => {
              setIsCreateFormOpen(false);
              listDocuments(pagination);
            }}
          />
        )}
      </SwipeableDrawer>

      {openDeleteDialog && (
        <AlertDialog
          content={
            <Grid
              container
              direction="row"
              alignItems="center"
              margin={'auto'}
              justifyContent={'center'}
              textAlign={'center'}>
              <Grid item xs={10} sm={8.7} md={8.7} lg={8.7} xl={8.7}>
                <span style={modalTextStyle}>
                  Are you sure you want to delete this document? This action can not be undone
                </span>
              </Grid>
            </Grid>
          }
          cancelText={'No, cancel'}
          approveText={documentState.delete.loading ? 'Deleting...' : 'Yes, delete'}
          disableButtons={documentState.delete.loading}
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onSave={handleConfirmDelete}
        />
      )}

      {openDownloadDialog && (
        <AlertDialog
          content={
            <Grid
              container
              direction="row"
              alignItems="center"
              margin={'auto'}
              justifyContent={'center'}
              textAlign={'center'}>
              <Grid item xs={10} sm={8.7} md={8.7} lg={8.7} xl={8.7}>
                <span style={modalTextStyle}>Are you sure you want to download this document?</span>
              </Grid>
            </Grid>
          }
          cancelText={'No, cancel'}
          approveText={
            storableAssetState.requestDownloadUrl.loading ? 'Downloading...' : 'Yes, download'
          }
          disableButtons={storableAssetState.requestDownloadUrl.loading}
          open={openDownloadDialog}
          onClose={() => setOpenDownloadDialog(false)}
          onSave={onConfirmDownload}
        />
      )}
    </>
  );
};

export default DocumentsList;

const modalTextStyle = { color: '#1F1F1F', fontSize: '14px' };
const swipeableDrawerStyle = {
  maxHeight: 'calc(100% - 64px)',
  '.MuiPaper-root': {
    background: '#fff',
    borderRadius: '8px 8px 0 0'
  }
};
